<template>
  <div class="auto-expanding-textarea-container">
    <textarea
      ref="textarea"
      :value="modelValue"
      class="auto-expanding-textarea"
      :class="{'action-buttons': actionButtons}"
      :placeholder="placeholder"
      @input="handleInput"
      @change="change"
      @keydown.ctrl.enter.prevent="$emit('submit')"
      @keydown.esc="$emit('cancel')"
    />
    <font-awesome-icon
      v-if="clearable && modelValue"
      :icon="['fal', 'times']"
      class="clear-icon"
      @click="clearInput"
    />
    <div v-if="actionButtons" class="textarea-actions">
      <BaseIconButton
        :icon="submitIcon"
        type="primary"
        @click="$emit('submit')"
      >
        {{ submitText }}
      </BaseIconButton>
      <BaseIconButton
        icon="times"
        type="cancel"
        @click="$emit('cancel')"
      >
        Avbryt
      </BaseIconButton>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import BaseIconButton from '@/components/BaseComponents/BaseIconButton.vue';

const { modelValue, placeholder, submitIcon, submitText, actionButtons, clearable } = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  submitIcon: {
    type: String,
    default: 'paper-plane',
  },
  submitText: {
    type: String,
    default: 'lagre',
  },
  actionButtons: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'submit', 'cancel', 'change']);

const textarea = ref(null);

function handleInput (event) {
  emit('update:modelValue', event.target.value);
  autoGrow();
}

function change (event) {
  emit('change', event.target.value);
}

function autoGrow () {
  // Reset height to auto to get the correct scrollHeight
  textarea.value.style.height = 'auto';
  // Set height to scrollHeight to expand
  textarea.value.style.height = textarea.value.scrollHeight + 'px';
}

function clearInput () {
  emit('update:modelValue', null);
  emit('change', null);
}

onMounted(() => {
  autoGrow();
});
</script>

<style scoped>
.auto-expanding-textarea-container {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.auto-expanding-textarea {
  width: 100%;
  min-height: 1.5rem;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: inherit;
  line-height: 1.5;
  resize: none;
  box-sizing: border-box;
  overflow: hidden; /* Hide scrollbar */
  flex-shrink: 0; /* Prevent shrinking but allow growing */
}

.auto-expanding-textarea.action-buttons {
  padding-right: 70px; /* Make room for buttons */
}

.auto-expanding-textarea:focus {
  outline: none;
  border-color: var(--primary-color);
}

.textarea-actions {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
}

.clear-icon {
  color: var(--gray-500);
  cursor: pointer;
  padding: 8px;
  position: absolute;
  right: 8px;
  top: 8px;
  pointer-events: auto;
  z-index: 2;
}

.clear-icon:hover {
  color: var(--gray-700);
}
</style>
